import { CreditNoteField } from '@components';
import { L } from '@lib';
import { Inventory, InventoryService } from '@services';
import { InputNumber, Modal, Typography, message } from 'antd';
import React, { useEffect, useState } from 'react';

const { Text } = Typography;

interface IProps {
  open: boolean;
  item: Inventory | null;
  onClose: () => void;
  onConfirmed: () => void;
}

/** Return stock directly to the supplier from an inventory item (no job involved). */
export const InventoryReturnModal: React.FC<IProps> = ({ open, item, onClose, onConfirmed }) => {
  const maxQty = item?.quantity ?? 0;
  const minQty = Math.min(1, maxQty || 1); // guards fractional stock (e.g. 0.5) where 1 would exceed max

  const [quantity, setQuantity] = useState(minQty);
  const [documentId, setDocumentId] = useState<string | undefined>();
  const [fileName, setFileName] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuantity(maxQty > 0 ? minQty : 0);
    setDocumentId(undefined);
    setFileName(undefined);
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async () => {
    if (!item) return;
    if (quantity <= 0 || quantity > maxQty) {
      message.warning(L('Enter a quantity between 1 and the stock on hand.'));
      return;
    }
    setSubmitting(true);
    try {
      await InventoryService.returnToSupplier({ inventoryId: item.id, quantity, documentId });
      message.success(L('Returned to supplier'));
      onConfirmed();
      onClose();
    } catch (e) {
      console.error(e);
      message.error(L('Failed to return to supplier.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={L('Return to supplier')}
      onCancel={onClose}
      okText={L('Return to supplier')}
      confirmLoading={submitting}
      onOk={handleConfirm}
      okButtonProps={{ disabled: !item || maxQty <= 0 }}
    >
      {item && (
        <>
          <p style={{ marginBottom: 12 }}>
            <Text strong>{item.name}</Text>{' '}
            <Text type="secondary">
              ({L('in stock')}: {maxQty})
            </Text>
          </p>

          <div style={{ marginBottom: 12 }}>
            <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>
              {L('Quantity to return')}
            </Text>
            <InputNumber
              min={minQty}
              max={maxQty}
              step={1}
              value={quantity}
              onChange={(v) => setQuantity(Number(v) || minQty)}
              style={{ width: 140 }}
            />
          </div>

          <CreditNoteField
            documentId={documentId}
            fileName={fileName}
            onChange={(id, name) => {
              setDocumentId(id);
              setFileName(name);
            }}
          />

          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            {L('If the credit note arrives later, attach it from the inventory movements history.')}
          </Text>
        </>
      )}
    </Modal>
  );
};

export default InventoryReturnModal;
